import React, { useRef } from 'react';
import { bool, node } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';
import ReCAPTCHA from 'react-google-recaptcha';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { Form, PrimaryButton, FieldTextInput, FieldCheckbox } from '../../../components';
import appSettings from '../../../config/settings';

import css from './SignupForm.module.css';

const SignupFormComponent = props => {
  const recaptchaRef = useRef(null);

  return (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        termsAndConditions,
          form,
      } = fieldRenderProps;

      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'SignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'SignupForm.emailInvalid',
        })
      );

      // password
      const passwordRequiredMessage = intl.formatMessage({
        id: 'SignupForm.passwordRequired',
      });
      const passwordMinLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooShort',
        },
        {
          minLength: validators.PASSWORD_MIN_LENGTH,
        }
      );
      const passwordMaxLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooLong',
        },
        {
          maxLength: validators.PASSWORD_MAX_LENGTH,
        }
      );
      const passwordMinLength = validators.minLength(
        passwordMinLengthMessage,
        validators.PASSWORD_MIN_LENGTH
      );
      const passwordMaxLength = validators.maxLength(
        passwordMaxLengthMessage,
        validators.PASSWORD_MAX_LENGTH
      );
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
      const passwordValidators = validators.composeValidators(
        passwordRequired,
        passwordMinLength,
        passwordMaxLength
      );

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      
      // Check if reCAPTCHA is required and completed
      const recaptchaToken = form.getState().values.recaptchaToken;
      const recaptchaRequired = appSettings.recaptcha.enabled && !recaptchaToken;
      
      const submitDisabled = invalid || submitInProgress || recaptchaRequired;

      // reCAPTCHA handlers
      const handleRecaptchaChange = value => {
        form.change('recaptchaToken', value);
      };

      const handleFormSubmit = async e => {
        // If reCAPTCHA is enabled, verify token before submitting
        if (appSettings.recaptcha.enabled) {
          const recaptchaToken = form.getState().values.recaptchaToken;
          if (!recaptchaToken) {
            // Prevent submission if reCAPTCHA is not completed
            e.preventDefault();
            return;
          }
        }
        handleSubmit(e);
      };

      return (
        <Form className={classes} onSubmit={handleFormSubmit}>
          <div>
            <FieldTextInput
              type="email"
              id={formId ? `${formId}.email` : 'email'}
              name="email"
              autoComplete="email"
              label={intl.formatMessage({
                id: 'SignupForm.emailLabel',
              })}
              placeholder={intl.formatMessage({
                id: 'SignupForm.emailPlaceholder',
              })}
              validate={validators.composeValidators(emailRequired, emailValid)}
            />

            <div className={css.name}>
              <FieldTextInput
                className={css.firstNameRoot}
                type="text"
                id={formId ? `${formId}.fname` : 'fname'}
                name="fname"
                autoComplete="given-name"
                label={intl.formatMessage({
                  id: 'SignupForm.firstNameLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.firstNamePlaceholder',
                })}
                validate={validators.required(
                  intl.formatMessage({
                    id: 'SignupForm.firstNameRequired',
                  })
                )}
              />
              <FieldTextInput
                className={css.lastNameRoot}
                type="text"
                id={formId ? `${formId}.lname` : 'lname'}
                name="lname"
                subHeading={intl.formatMessage({ id: 'SignupForm.SignupForm.lastNameShowTip' })}
                autoComplete="family-name"
                isSignup={true}
                label={intl.formatMessage({
                  id: 'SignupForm.lastNameLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.lastNamePlaceholder',
                })}
                validate={validators.required(
                  intl.formatMessage({
                    id: 'SignupForm.lastNameRequired',
                  })
                )}
              />
            </div>
            {/* <p className={css.tip}></p> */}
            <FieldTextInput
              className={css.password}
              type="password"
              id={formId ? `${formId}.password` : 'password'}
              name="password"
              autoComplete="new-password"
              label={intl.formatMessage({
                id: 'SignupForm.passwordLabel',
              })}
              placeholder={intl.formatMessage({
                id: 'SignupForm.passwordPlaceholder',
              })}
              validate={passwordValidators}
            />
          </div>

          <div className={css.bottomWrapper}>
                        <FieldCheckbox
              className={css.newsletter}
              id="newsletter"
              name="newsletter"
              label={
                <span className={css.newsletterText}>
                  {intl.formatMessage({ id: 'SignupForm.SignupForm.newLetterText' })} <br />
                  {intl.formatMessage({ id: 'SignupForm.SignupForm.newLetterTextTwo' })}
                </span>
              }
            />
            
            {/* Google reCAPTCHA */}
            {appSettings.recaptcha.enabled && (
              <div className={css.recaptchaWrapper}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={appSettings.recaptcha.siteKey}
                  onChange={handleRecaptchaChange}
                />
              </div>
            )}

            <PrimaryButton
              className={css.submit}
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
            >
              <FormattedMessage id="SignupForm.signUp" />
            </PrimaryButton>
            {termsAndConditions}
          </div>
        </Form>
      );
    }}
  />
);
};

SignupFormComponent.defaultProps = { inProgress: false };

SignupFormComponent.propTypes = {
  inProgress: bool,
  termsAndConditions: node.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const SignupForm = compose(injectIntl)(SignupFormComponent);
SignupForm.displayName = 'SignupForm';

export default SignupForm;
